import { concat as observableConcat, BehaviorSubject, Observable, Subscription, Subject } from 'rxjs';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';

import { BaseControlComponent } from "../../../../../../form-controls/components/base-control/base-control.component";

import { Dialog, DialogButton } from "../../../../../../core/models/dialog.model";
import { DynamicDialogService } from "../../../../../../core/services/dynamic-dialog.service";
import { DocumentManagementService, IAzureBlobSasToken, IUploadFile } from '../../../../../../common/services/document-management.service';
import { LoaderService } from "../../../../../../common/services/loader.service";
import { TemplateService } from "../../../../../../common/components/pdf-viewer/services/template.service";

import { DomainValue, TagDefinition } from '../../../../../../common/models/document-tag.model';
import { FileTagsConfiguration, TagSelector } from '../../../../../../builder/models/file-tag-configuration.model';
import { Rule } from "../../../../../../common/models/flow.dto";

import { CustomHeader, deepClone, PdfViewerConfig, TemplatePdfOptions, TemplateMarginOptions } from "../../../../../models/editor.model";
import { ContextInteractionService } from '../../../../../../form-controls/services/context-interaction.service';

@Component({
    selector: 'app-flow-pdf-viewer-data-config',
    templateUrl: './flow-pdf-viewer-data-config.component.html',
    styleUrls: ['./flow-pdf-viewer-data-config.component.scss'],
    standalone: false
})
export class FlowPdfViewerDataConfigComponent extends BaseControlComponent implements OnInit, OnDestroy {

    public static componentName = 'FlowPdfViewerDataConfigComponent';
    public attachments$: BehaviorSubject<IUploadFile[]> = new BehaviorSubject([]);
    public defaultAttachment: IUploadFile = {
        canBeDeleted: true,
        sentToOnbase: false,
        rawFile: null,
        completed: true,
        isUploading: false,
        waiting: false,
        percentage: 100,
        path: "",
        relativePath: "",
        fileName: "--Select a file--",
        blobUri: "",
        type: ""
    }
    public defaultCustomHeader: CustomHeader = {
        headerText: "",
        compileAsExpression: false,
        expressionRule: undefined
    } as CustomHeader;
    public defaultOptions: TemplatePdfOptions = {
        mergeDocuments: false,
        useDefaults: true,
        landscape: false,
        paperFormat: "letter",
        headerTemplate: "",
        footerTemplate: "",
        marginOptions: { top: 480, bottom: 360, left: 360, right: 360 } as TemplateMarginOptions,
        useCustomHeader: false,
        customHeader: this.defaultCustomHeader,
        addStaticDocuments: false,
        appendStaticDocumentsFirst: false,
        documentTypeContext: "",
        staticFiles: []
    } as TemplatePdfOptions;

    public filesInProcess: IUploadFile[] = [];
    public fileName: string;

    public selectedAttachment: IUploadFile = this.defaultAttachment;
    public subscriptions: Subscription = new Subscription();


    @ViewChild('input', { read: ElementRef }) public _fileUploadElement: ElementRef;

    get addStaticDocuments(): boolean {
        if (this.componentData.config.pdfOptions.addStaticDocuments === undefined) {
            this.componentData.config.pdfOptions.addStaticDocuments = false;
        }
        return this.componentData.config.pdfOptions.addStaticDocuments;
    }

    set addStaticDocuments(value: boolean) {
        this.componentData.config.pdfOptions.addStaticDocuments = value;
    }

    get appendStaticDocumentsFirst(): boolean {
        return this.componentData.config.pdfOptions.appendStaticDocumentsFirst;
    }

    set appendStaticDocumentsFirst(value: boolean) {
        this.componentData.config.pdfOptions.appendStaticDocumentsFirst = value;
    }

    get compileAsExpression(): boolean {
        return this.componentData.config.pdfOptions.customHeader.compileAsExpression;
    }

    set compileAsExpression(value: boolean) {
        this.componentData.config.pdfOptions.customHeader.compileAsExpression = value;
    }

    get pdfType(): string {
        return this.componentData.config.pdfDocument;
    }

    set pdfType(value: string) {
        this.componentData.config.pdfDocument = value;
    }

    get pdfTypeContext(): string {
        if (!this.componentData.config.pdfDocumentContext) {
            this.componentData.config.pdfDocumentContext = "";
        }
        return this.componentData.config.pdfOptions.documentTypeContext;
    }

    set pdfTypeContext(value: string) {
        this.componentData.config.pdfOptions.documentTypeContext = value;
    }

    get permitDataContext(): string {
        if (!this.componentData.config.permitDataLocation) {
            this.componentData.config.permitDataLocation = "";
        }
        return this.componentData.config.permitDataLocation;
    }

    set permitDataContext(value: string) {
        this.componentData.config.permitDataLocation = value;
    }

    get wellDataContext(): string {
        if (!this.componentData.config.wellDataLocation) {
            this.componentData.config.wellDataLocation = "";
        }
        return this.componentData.config.wellDataLocation;
    }

    set wellDataContext(value: string) {
        this.componentData.config.wellDataLocation = value;
    }

    get contactDataContext(): string {
        if (!this.componentData.config.contactDataLocation) {
            this.componentData.config.contactDataLocation = "";
        }
        return this.componentData.config.contactDataLocation;
    }

    set contactDataContext(value: string) {
        this.componentData.config.contactDataLocation = value;
    }

    get organizationIdLocation(): string {
        if (!this.componentData.config.organizationIdLocation) {
            this.componentData.config.organizationIdLocation = "$.organizationId";
        }
        return this.componentData.config.organizationIdLocation;
    }

    set organizationIdLocation(value: string) {
        this.componentData.config.organizationIdLocation = value;
    }


    get useCustomHeader(): boolean {
        return this.componentData.config.pdfOptions.useCustomHeader;
    }

    set useCustomHeader(value: boolean) {
        this.componentData.config.pdfOptions.useCustomHeader = value;
    }

    get mergeDocuments(): boolean {
        return this.componentData.config.pdfOptions.mergeDocuments;
    }

    set mergeDocuments(value: boolean) {
        this.componentData.config.pdfOptions.mergeDocuments = value;
    }

    get returnToOwnerDate(): string {
        if (this.componentData.config.returnToOwnerDate == null) {
            this.componentData.config.returnToOwnerDate = "";
        }
        return this.componentData.config.returnToOwnerDate;
    }

    set returnToOwnerDate(value: string) {
        this.componentData.config.returnToOwnerDate = value;
    }

    get bondNumber(): string {
        if (this.componentData.config.bondNumber == null) {
            this.componentData.config.bondNumber = "";
        }
        return this.componentData.config.bondNumber;
    }

    set bondNumber(value: string) {
        this.componentData.config.bondNumber = value;
    }

    get bondReleaseDate(): string {
        if (this.componentData.config.bondReleaseDate == null) {
            this.componentData.config.bondReleaseDate = "";
        }
        return this.componentData.config.bondReleaseDate;
    }

    set bondReleaseDate(value: string) {
        this.componentData.config.bondReleaseDate = value;
    }

    get bondTypeReleased(): string {
        if (this.componentData.config.bondTypeReleased == null) {
            this.componentData.config.bondTypeReleased = "";
        }
        return this.componentData.config.bondTypeReleased;
    }

    set bondTypeReleased(value: string) {
        this.componentData.config.bondTypeReleased = value;
    }

    get domainTranslateBondType(): boolean {
        if (this.componentData.config.domainTranslateBondType == null) {
            this.componentData.config.domainTranslateBondType = false;
        }
        return this.componentData.config.domainTranslateBondType;
    }

    set domainTranslateBondType(value: boolean) {
        this.componentData.config.domainTranslateBondType = value;
    }

    get bondTypeDomain(): any {
        if (this.componentData.config.bondTypeDomain == null) {
            this.componentData.config.bondTypeDomain = {
                config: {
                    domain: "",
                    idProperty: "",
                    displayProperty: ""
                }
            };
        }
        return this.componentData.config.bondTypeDomain;
    }

    set bondTypeDomain(value: any) {
        this.componentData.config.bondTypeDomain = value;
    }

    get sourceApplicationType(): string {
        if (this.componentData.config.sourceApplicationType == null) {
            this.componentData.config.sourceApplicationType = "";
        }
        return this.componentData.config.sourceApplicationType;
    }

    set sourceApplicationType(value: string) {
        this.componentData.config.sourceApplicationType = value;
    }

    dialog: Dialog = new Dialog("You have uploaded a disallowed file type.  The file upload has been cancelled.  Please select a different file and try again.", [new DialogButton("OK", true, () => this.dialogClose())]);

    constructor(public readonly dialogService: DynamicDialogService,
        public readonly documentManagementService: DocumentManagementService,
        public readonly templateService: TemplateService,
        public readonly loader: LoaderService,
        public readonly contextService: ContextInteractionService) {
        super(contextService);
    }

    ngOnDestroy() {
        if (this.subscriptions)
            this.subscriptions.unsubscribe();
    }

    ngOnInit() {
        super.ngOnInit();
        if (!this.componentData.config) {
            this.componentData.config = {
            pdfDocument: " ", pdfOptions: this.defaultOptions, tags: [], documentType: null } as PdfViewerConfig;
        }  
        if (this.componentData.config.htmlTemplate) {
            this.componentData.config.htmlTemplate = undefined;
        }
        if (this.componentData.config && !this.componentData.config.pdfDocument) {
            this.componentData.config.pdfDocument = " ";
        }    
        if (this.componentData.config && !this.componentData.config.pdfOptions) {
            this.componentData.config.pdfOptions = this.defaultOptions;
        } else {
            if (this.componentData.config.pdfOptions.mergeDocuments === undefined) {
                this.componentData.config.pdfOptions.mergeDocuments = false;
            }
            if (this.componentData.config.pdfOptions.useCustomHeader === undefined) {
                this.componentData.config.pdfOptions.useCustomHeader = false;
            }
            if (this.componentData.config.pdfOptions.customHeader === undefined) {
                this.componentData.config.pdfOptions.customHeader = this.defaultCustomHeader;
            }
        }
        if (this.componentData.config && !this.componentData.config.tags) {
            this.componentData.config.tags = [];
        }
        if (this.componentData.config && !this.componentData.config.documentType) {
            this.componentData.config.documentType = null;
        }
        if (this.componentData.config && !this.componentData.config.pdfOptions.staticFiles) {
            this.componentData.config.pdfOptions.staticFiles = [];
        }

        this.fileName = this.componentData.config.templateFile;
        this.selectedAttachment = this.defaultAttachment;
        this.getAvailableAttachments();
        if (this.pdfType != null && this.pdfType !== "") {
            this.setDefaultContextLocations(this.pdfType);
        }
    }

    public addFile(e: Event): void {
        this.loader.toggleLoader(true);
        const files = (event.srcElement as any).files;
        const fileSubscription = this.documentManagementService.whiteList.subscribe(list => {
            const extensions = [];
            for (let fi = 0; fi < files.length; fi++) {
                const parts = files[fi].name.split(".");
                if (parts.length > 1) {
                    extensions.push(parts[parts.length - 1]);
                } else {
                    extensions.push("");
                }
            }
            this.loader.toggleLoader(false);
            const result = extensions.every(f => list.indexOf(f) !== -1);
            if (result) {
                this.addFilesToQueue(files);
            } else {
                this.dialog.toggleDialog(true);
                this.dialogService.updateDialog(this.dialog);
            }
            this._fileUploadElement.nativeElement.value = "";
        });
        this.subscriptions.add(fileSubscription);
    }

    public addFileToConfig(): void {
        const file = this.selectedAttachment;
        const existingIndex = this.componentData.config.pdfOptions.staticFiles.findIndex(f => f.fileName === file.fileName && f.relativePath == file.relativePath);
        if (existingIndex === -1) {
            this.componentData.config.pdfOptions.staticFiles.push(file);
        }
        this.selectedAttachment = this.defaultAttachment;
    }

    public addFilesToQueue(files: FileList): void {
        for (let i = 0; i < files.length; i++) {
            if (!this._fileExists(files[i]))
            this.filesInProcess.push({ rawFile: files[i], completed: false, waiting: true, isUploading: false, percentage: 0, fileName: files.item(i).name, type: files.item(i).type } as IUploadFile);
        }
        this.processFiles();
    }

    public clearData(): void {
        this.componentData.config.htmlTemplate = " ";
    }

    public consolidateFileLocation(indexedFile: IUploadFile): void {
        const fileList = [];
        for (let f = 0; f < this.componentData.config.pdfOptions.staticFiles.length; f++) {
            const matches = this.componentData.config.pdfOptions.staticFiles[f].fileName === indexedFile.fileName;
            if (matches) {
                fileList.push(indexedFile);
            } else {
                fileList.push(this.componentData.config.pdfOptions.staticFiles[f]);
            }
        }
        this.componentData.config.pdfOptions.staticFiles = fileList;
    }

    public deleteFile(file: IUploadFile): void {
        const index = this.componentData.config.pdfOptions.staticFiles.findIndex(x => x.blobUri === file.blobUri);
        if (index !== -1) {
            this.componentData.config.pdfOptions.staticFiles.splice(index, 1);
        }
    }

    public determineFileType(fileName: string): string {
        let fileType = "";
        const parts = fileName.split(".");
        const extension = parts[parts.length - 1];
        switch (extension.toLowerCase()) {
            case "pdf":
                fileType = "application/pdf";
            break;
            case "doc":
                fileType = "application/msword";
            break;
            case "docx":
                fileType = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            break;
            case "xls":
                fileType = "application/vnd.ms-excel";
            break;
            case "xlsx":
                fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            break;
            default:
                fileType = "";
            break;
        }
        return fileType;
    }

    public getAvailableAttachments(): void {
        const attachmentSubscription = this.documentManagementService.getProvisionedAttachments().subscribe(attachments => {
            const list = [];
            list.push(this.selectedAttachment);
            for (let a = 0; a < attachments.length; a++) {
                const file: IUploadFile = {
                    rawFile : null,
                    completed : true,
                    isUploading : false,
                    percentage : 100,
                    blobUri : attachments[a].blobUri,
                    path : attachments[a].blobUri,
                    type: this.determineFileType(attachments[a].fileName),
                    fileName : attachments[a].fileName
                };
                file.relativePath = attachments[a].blobUri.replace(attachments[a].storageUri, "");
                list.push(file);
                const existingIndex = this.componentData.config.pdfOptions.staticFiles.findIndex(f => f.fileName === file.fileName);
                if (existingIndex !== -1 && this.componentData.config.pdfOptions.staticFiles[existingIndex].relativePath != file.relativePath) {
                    this.consolidateFileLocation(file);
                }
            }
            this.attachments$.next(list);
        });
        this.subscriptions.add(attachmentSubscription);
    }

    public ruleCompiled(rule: Rule): void {
        this.componentData.config.pdfOptions.customHeader.headerText = rule.expression;
        this.componentData.config.pdfOptions.customHeader.expressionRule = rule;
    }

    public _fileExists(file: File): boolean {
        for (var f of this.componentData.config.pdfOptions.staticFiles) {
            if (f.rawFile.lastModified === file.lastModified && f.rawFile.name === file.name &&
            f.rawFile.size === file.size && f.rawFile.type === file.type) {
            return true;
            }
        }

        return false;
    }

    public processFiles(): void {
        if (!this.filesInProcess.length) return;
        const files = this.filesInProcess.filter(x => !x.completed && !x.isUploading);
        if (files.length === 0) return;
        let obs: Observable<IAzureBlobSasToken>[] = [];
        files.forEach(x => { obs.push(this.uploadAttachmentFile(x)); });

        observableConcat(...obs).subscribe((resp) => {
            const existing = this.componentData.config.pdfOptions.staticFiles.find(f => f.blobUri === resp.blobUri);
            const relativePath = resp.blobUri.replace(resp.storageUri, "");
            if (existing) {
                existing.relativePath = relativePath;
            } else {
                const file = files.find(f => f.blobUri === resp.blobUri);
                if (file) {
                    file.relativePath = relativePath;
                    this.selectedAttachment = file;
                    this.addFileToConfig();
                }
            }
        });
    }

    public uploadAttachmentFile(file: IUploadFile): Observable<IAzureBlobSasToken> {
        const uploadComplete = new Subject<IAzureBlobSasToken>();
        this.documentManagementService.provisionAttachment(file.rawFile.name).subscribe((x) => {
            this.documentManagementService.processProvisionedFile(file, x, uploadComplete);
        }, (error) => {
                if (this.documentManagementService.WHITELIST_ERRORS.indexOf(error.error) !== -1) {
                    this.dialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.dialog);
            } else {
                console.error(error);
            }
        });
        return uploadComplete.asObservable();
    }

    public dialogClose(): void {
    }

    pdfTypeChanged(evt: string): void {
        this.setDefaultContextLocations(evt);
    }

    setDefaultContextLocations(documentType: string): void {
        switch (documentType) {
            case "PermitToDrill":
            case "Context":
                this.permitDataContext = "$.permit";
                this.wellDataContext = "$.well";
                this.contactDataContext = "$.data.permitContact.contact";
                break;
            case "PermitToPlug":
                this.permitDataContext = "$.permit";
                this.wellDataContext = "$.data.well.value[0]";
                this.contactDataContext = "$.data.loggedInUser.contact";
                break;
            default:
                this.permitDataContext = "";
                this.wellDataContext = "";
                this.contactDataContext = "";
                break;
        }
        
    }
}
